import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { TimeSignature } from '../lib/rhythm/ecoRitmoConfig';
import {
  DEFAULT_ECO_LAYOUT_STORE,
  allPresetKeys,
  cssVarsForSession,
  getPreset,
  loadEcoLayoutTuning,
  presetKey,
  saveEcoLayoutTuning,
  type EcoLayoutPreset,
  type EcoLayoutTuningStore,
  type EcoPresetKey,
  type EcoSlotTuning,
} from '../lib/rhythm/ecoLayoutTuning';

interface EcoLayoutTuningContextValue {
  store: EcoLayoutTuningStore;
  cssVarsFor: (ts: TimeSignature, measureCount: number) => Record<string, string>;
  presetFor: (ts: TimeSignature, measureCount: number) => EcoLayoutPreset;
  slotTuningFor: (ts: TimeSignature) => EcoSlotTuning;
  updatePreset: (key: EcoPresetKey, patch: Partial<EcoLayoutPreset>) => void;
  updateSlots: (ts: TimeSignature, patch: Partial<EcoSlotTuning>) => void;
  resetAll: () => void;
  persist: () => void;
  presetKeys: EcoPresetKey[];
}

const EcoLayoutTuningContext = createContext<EcoLayoutTuningContextValue | null>(null);

export function EcoLayoutTuningProvider({ children }: { children: React.ReactNode }) {
  const [store, setStore] = useState<EcoLayoutTuningStore>(() => loadEcoLayoutTuning());

  const cssVarsFor = useCallback(
    (ts: TimeSignature, measureCount: number) => cssVarsForSession(store, ts, measureCount),
    [store]
  );

  const presetFor = useCallback(
    (ts: TimeSignature, measureCount: number) => getPreset(store, ts, measureCount),
    [store]
  );

  const slotTuningFor = useCallback(
    (ts: TimeSignature) => store.slots[ts] ?? DEFAULT_ECO_LAYOUT_STORE.slots[ts],
    [store.slots]
  );

  const updatePreset = useCallback((key: EcoPresetKey, patch: Partial<EcoLayoutPreset>) => {
    setStore((prev) => ({
      ...prev,
      presets: {
        ...prev.presets,
        [key]: { ...prev.presets[key], ...patch },
      },
    }));
  }, []);

  const updateSlots = useCallback((ts: TimeSignature, patch: Partial<EcoSlotTuning>) => {
    setStore((prev) => ({
      ...prev,
      slots: {
        ...prev.slots,
        [ts]: { ...prev.slots[ts], ...patch },
      },
    }));
  }, []);

  const resetAll = useCallback(() => {
    setStore(DEFAULT_ECO_LAYOUT_STORE);
    saveEcoLayoutTuning(DEFAULT_ECO_LAYOUT_STORE);
  }, []);

  const persist = useCallback(() => {
    saveEcoLayoutTuning(store);
  }, [store]);

  const value = useMemo(
    () => ({
      store,
      cssVarsFor,
      presetFor,
      slotTuningFor,
      updatePreset,
      updateSlots,
      resetAll,
      persist,
      presetKeys: allPresetKeys(),
    }),
    [store, cssVarsFor, presetFor, slotTuningFor, updatePreset, updateSlots, resetAll, persist]
  );

  return (
    <EcoLayoutTuningContext.Provider value={value}>{children}</EcoLayoutTuningContext.Provider>
  );
}

export function useEcoLayoutTuning(): EcoLayoutTuningContextValue {
  const ctx = useContext(EcoLayoutTuningContext);
  if (!ctx) {
    throw new Error('useEcoLayoutTuning must be used within EcoLayoutTuningProvider');
  }
  return ctx;
}

export { presetKey };
